import { Briefcase, Building2, Clock, FileText } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { useBillingNouns } from '@/hooks/useSession';
import { cn } from '@/lib/utils';

interface Row {
  icon: ReactNode;
  noun: string;
  what: string;
  example: string;
}

/**
 * Step 1 of the guide: four rows over the timesheet. Case-based teams see a
 * client as the case and the billing party as who pays; projects-mode teams
 * see the project as the engagement, invoiced to the client directly.
 */
export function HowItWorksCard({
  onNext,
  onSkip,
  total,
  nextLabel,
  className
}: {
  /** Advances the rail to step 2; this step is already done once the state exists. */
  onNext: () => void;
  onSkip: () => void;
  /** How many steps this person has (7 for admins, 3 for members). */
  total: number;
  /** "Next: your firm" for admins, "Next: log time" for members. */
  nextLabel: string;
  className?: string;
}) {
  const nouns = useBillingNouns();
  const direct = nouns.client === 'Project';

  const rows: Row[] = direct
    ? [
        {
          icon: <Briefcase />,
          noun: 'Project',
          what: 'The engagement. One per piece of work.',
          example: 'Brand refresh'
        },
        {
          icon: <Building2 />,
          noun: 'Billing contact',
          what: 'The client. Invoiced directly.',
          example: 'Northline Studio'
        },
        {
          icon: <Clock />,
          noun: 'Time',
          what: 'Project, activity, hours.',
          example: 'Brand refresh, Design, 2.5h'
        },
        {
          icon: <FileText />,
          noun: 'Invoice',
          what: "A project's unbilled time, sent to the client.",
          example: 'INV-0001, $1,250'
        }
      ]
    : [
        {
          icon: <Briefcase />,
          noun: 'Client',
          what: 'The case. One per matter.',
          example: 'Vega v. Harlow'
        },
        {
          icon: <Building2 />,
          noun: 'Billing party',
          what: 'Who pays. The law firm that retained you, shared across cases.',
          example: 'Carver & Pell LLP'
        },
        {
          icon: <Clock />,
          noun: 'Time',
          what: 'Case, activity, hours.',
          example: 'Vega v. Harlow, Research, 1.5h'
        },
        {
          icon: <FileText />,
          noun: 'Invoice',
          what: "A case's unbilled time, sent to the law firm.",
          example: 'INV-0001, $525'
        }
      ];

  return (
    <div
      role="dialog"
      aria-labelledby="setup-how-title"
      className={cn(
        'w-full max-w-lg rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-6 shadow-xl',
        className
      )}
    >
      <p className="text-xs font-medium uppercase tracking-wide text-muted">Step 1 of {total}</p>
      <h2 id="setup-how-title" className="mt-1 text-lg font-semibold tracking-tight">
        How Billable works
      </h2>
      <ul className="mt-5 divide-y divide-[var(--line)]">
        {rows.map((row) => (
          <li key={row.noun} className="flex gap-3 py-3">
            <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-[var(--accent-soft)] text-[var(--accent)] [&_svg]:size-4">
              {row.icon}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">{row.noun}</p>
              <p className="text-sm text-muted">{row.what}</p>
            </div>
            <p className="shrink-0 pl-2 text-right text-xs text-subtle">{row.example}</p>
          </li>
        ))}
      </ul>
      <div className="mt-5 flex items-center justify-between gap-2">
        <Button variant="ghost" size="sm" onClick={onSkip}>
          Skip the tour
        </Button>
        <Button variant="primary" onClick={onNext}>
          {nextLabel}
        </Button>
      </div>
    </div>
  );
}
